// Lógica pura de Administración · Equipo y accesos (T-jre9): quién puede
// qué según la receta y la sesión, la columna «Acceso» a partir del
// roster más los alcances de core-api, la tabla de roles, el parseo de
// correos del drawer de invitar, el diff del selector de tiendas, las
// filas y conteos del drawer de invitar en lote y el mapa de códigos de
// error a frase. Sin React, para probarla sola.
//
// Toda capacidad sale de la receta (memberRoles[].orgAdmin, scopeType,
// observationStatusActor, brandIdentity) o de los predicados de
// @bitacorapro/api-types; nunca del slug.

import {
  INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER,
  invitationGrantDenial,
  isAssetScopedInviteRole,
  ROLE_KEYS,
  type InvitationBatchCommitRequest,
  type InvitationBatchCommitResponse,
  type InvitationBatchPreviewResponse,
  type InvitationBatchPreviewRow,
  type MemberScopeItem,
  type OrgInvitationListItem,
  type OrgMember,
  type OrgSelfServiceInvitationCreateRequest,
  type RecipeMeResponse,
  type Role,
} from "@bitacorapro/api-types";
import { isOrgAdminRole } from "@bitacorapro/core-engine";
import { roleLabelFor } from "../../../lib/admin/words";
import { ApiError } from "../../../lib/api/error";
import { lastSeenLabel, normalizeSearch } from "../../../lib/util";

// Lo que esta pantalla lee de la receta. Parcial a propósito: los tests
// y las pantallas vecinas pueblan sólo lo que leen.
export type EquipoRecipe = Pick<RecipeMeResponse, "memberRoles"> &
  Partial<Pick<RecipeMeResponse, "labels" | "brands">>;

type MemberRoleEntry = EquipoRecipe["memberRoles"][number];

function lower(s: string): string {
  return s.toLocaleLowerCase("es-CL");
}

export type EquipoWords = {
  /** «Tienda» / «tienda». */
  assetOne: string;
  assetOneLower: string;
  /** «Tiendas» / «tiendas». */
  assetMany: string;
  assetManyLower: string;
  /** «Zona» / «zona». */
  groupOne: string;
  groupOneLower: string;
  /** «Zonas» / «zonas». */
  groupMany: string;
  groupManyLower: string;
  /** «Marca» / «marca». */
  brand: string;
  brandLower: string;
};

export function equipoWords(recipe: EquipoRecipe | undefined): EquipoWords {
  const assetOne = recipe?.labels?.asset ?? "Activo";
  const assetMany = recipe?.labels?.assetPlural ?? "Activos";
  const groupOne = recipe?.labels?.assetGroup ?? "Grupo";
  const groupMany = recipe?.labels?.assetGroupPlural ?? "Grupos";
  const brand = recipe?.brands?.label ?? "Marca";
  return {
    assetOne,
    assetOneLower: lower(assetOne),
    assetMany,
    assetManyLower: lower(assetMany),
    groupOne,
    groupOneLower: lower(groupOne),
    groupMany,
    groupManyLower: lower(groupMany),
    brand,
    brandLower: lower(brand),
  };
}

export function memberInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

function roleEntry(recipe: EquipoRecipe | undefined, role: string): MemberRoleEntry | undefined {
  return recipe?.memberRoles.find((r) => r.key === role);
}

/** Quien administra la organización: `admin` (staff BitácoraPro dentro
 *  de la org) o el rol que la receta declara `orgAdmin`. */
export function administersOrgRole(recipe: EquipoRecipe | undefined, role: string): boolean {
  if (role === "admin") return true;
  if (!recipe) return false;
  return isOrgAdminRole({ memberRoles: recipe.memberRoles }, role);
}

/** La clase del pill de rol (handoff §3): `--admin` para quien
 *  administra, `--sup` para quien asigna sin administrar
 *  (assigning_party), sin modificador para el resto. */
export function rolePillClass(recipe: EquipoRecipe | undefined, role: string): string {
  if (administersOrgRole(recipe, role)) return "role-pill role-pill--admin";
  if (roleEntry(recipe, role)?.observationStatusActor === "assigning_party") {
    return "role-pill role-pill--sup";
  }
  return "role-pill";
}

/** Un rol cuyo acceso se edita por tiendas: lo declara la receta con
 *  alcance de tienda o de zona Y su invitación estampa tiendas
 *  (isAssetScopedInviteRole). Es el mismo par que
 *  PUT /organizations/me/members/:userId/scope exige antes de aceptar un
 *  cambio (ROLE_NOT_ASSET_SCOPED). */
export function isStoreScopedRole(recipe: EquipoRecipe | undefined, role: string): boolean {
  const entry = roleEntry(recipe, role);
  if (!entry) return false;
  if (!(ROLE_KEYS as readonly string[]).includes(role)) return false;
  if (!isAssetScopedInviteRole(role as Role)) return false;
  return entry.scopeType === "asset" || entry.scopeType === "asset_group";
}

/** El modo del selector de tiendas en «Editar acceso»: una sola tienda
 *  para el rol que la receta declara con alcance `asset` (el jefe),
 *  varias para el de zona. Sale del scopeType, no de la clave del rol. */
export function storePickerMode(
  recipe: EquipoRecipe | undefined,
  role: string
): "single" | "multi" {
  return roleEntry(recipe, role)?.scopeType === "asset" ? "single" : "multi";
}

export type Me = {
  userId: string | undefined;
  role: string | undefined;
  platformAdmin: boolean;
};

/** Si la fila ofrece «Desactivar»/«Reactivar». Además de administrar la
 *  org (lo decide la pantalla con canAdministerOrg), el backend rechaza
 *  la fila propia (SELF_DEACTIVATION_FORBIDDEN), la del dueño
 *  (OWNER_DEACTIVATION_FORBIDDEN) y, para un administrador de receta,
 *  la de otro administrador (ADMIN_REQUIRED); esas no muestran el botón.
 *  Sin `membershipId` (servidor anterior) tampoco: no habría a qué
 *  pegarle el PATCH. */
export function canChangeMemberState(
  recipe: EquipoRecipe | undefined,
  member: OrgMember,
  me: Me
): boolean {
  if (!member.membershipId) return false;
  if (member.userId === me.userId) return false;
  if (member.isOwner) return false;
  if (administersOrgRole(recipe, member.role)) {
    return me.platformAdmin || me.role === "admin";
  }
  return true;
}

/** Reenviar y revocar: quien administra la org, o quien creó la
 *  invitación (un supervisor sobre las suyas). */
export function canActOnInvitation(
  inv: OrgInvitationListItem,
  me: Me,
  canAdminister: boolean
): boolean {
  return canAdminister || (me.userId !== undefined && inv.invitedByUserId === me.userId);
}

// --- Columna «Acceso» ---------------------------------------------------

export type AccessContext = {
  recipe: EquipoRecipe | undefined;
  /** El alcance de core-api para este usuario; ausente = nada a cargo. */
  scope: MemberScopeItem | undefined;
  /** id → nombre de zonas, tiendas y marcas (POST /assets/resolve y
   *  GET /organizations/me.brands). */
  names: ReadonlyMap<string, string>;
  /** Tiendas por zona (GET /assets/assignable); null mientras no cargó o
   *  cuando el caller no ve la organización entera (esa lista está acotada
   *  a su alcance y el conteo saldría parcial): entonces la zona se
   *  muestra sin tiendas. */
  zoneStoreCount: ReadonlyMap<string, number> | null;
};

function uniq(ids: ReadonlyArray<string | null | undefined>): string[] {
  return [...new Set(ids.filter((id): id is string => typeof id === "string" && id.length > 0))];
}

function countStores(n: number, w: EquipoWords): string {
  return n === 1 ? `1 ${w.assetOneLower}` : `${n} ${w.assetManyLower}`;
}

export const ORG_WIDE_ACCESS = "Toda la organización";

/** «Zona Norte · 5 tiendas», «AS-014», «Astoria · lectura», «Toda la
 *  organización», «Sin tiendas asignadas». Las tiendas y zonas directas
 *  del roster (scopeAssetId / scopeAssetGroupId) se suman a las de
 *  core-api por si un servidor sólo manda unas. */
export function accessLabel(member: OrgMember, ctx: AccessContext): string {
  const w = equipoWords(ctx.recipe);
  if (administersOrgRole(ctx.recipe, member.role)) return ORG_WIDE_ACCESS;
  const entry = roleEntry(ctx.recipe, member.role);
  if (entry?.scopeType === "brand" || member.scopeBrandId) {
    const name = member.scopeBrandId ? ctx.names.get(member.scopeBrandId) : undefined;
    return `${name ?? `Su ${w.brandLower}`} · lectura`;
  }
  const stores = uniq([...(ctx.scope?.assetIds ?? []), member.scopeAssetId]);
  const zones = uniq([...(ctx.scope?.assetGroupIds ?? []), member.scopeAssetGroupId]);
  const none = `Sin ${w.assetManyLower} asignadas`;

  if (entry?.scopeType === "asset_group" || zones.length > 0) {
    if (zones.length > 0) {
      const zoneText =
        zones.length === 1
          ? (ctx.names.get(zones[0]!) ?? `1 ${w.groupOneLower}`)
          : `${zones.length} ${w.groupManyLower}`;
      const inZones = zones.reduce((n, z) => n + (ctx.zoneStoreCount?.get(z) ?? 0), 0);
      const n = stores.length + inZones;
      return n > 0 ? `${zoneText} · ${countStores(n, w)}` : zoneText;
    }
    return stores.length > 0 ? countStores(stores.length, w) : none;
  }
  if (entry?.scopeType === "organization") return ORG_WIDE_ACCESS;
  if (stores.length === 1) return ctx.names.get(stores[0]!) ?? `1 ${w.assetOneLower}`;
  if (stores.length > 1) return countStores(stores.length, w);
  return none;
}

/** «hoy», «hace 3 días», «—» si nunca entró. */
export function lastActivityLabel(lastSeenAt: string | null | undefined, now?: Date): string {
  if (!lastSeenAt) return "—";
  return lastSeenLabel(lastSeenAt, now);
}

/** «27 oct»: la columna «Enviada» de las invitaciones. */
export function fmtEnviada(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CL", { day: "numeric", month: "short" })
    .format(d)
    .replace(/\.$/, "");
}

// --- Búsqueda local -----------------------------------------------------

export function filterMembers(members: readonly OrgMember[], q: string): OrgMember[] {
  const needle = normalizeSearch(q.trim());
  if (!needle) return [...members];
  return members.filter((m) => normalizeSearch(`${m.displayName} ${m.email}`).includes(needle));
}

export function filterInvitations(
  invitations: readonly OrgInvitationListItem[],
  q: string
): OrgInvitationListItem[] {
  const needle = normalizeSearch(q.trim());
  if (!needle) return [...invitations];
  return invitations.filter((i) =>
    normalizeSearch(`${i.email ?? ""} ${i.inviteeName ?? ""}`).includes(needle)
  );
}

// --- Tab Invitaciones ---------------------------------------------------

export const OPEN_INVITATION_LABEL = "Cualquier persona con el enlace";

export function invitationEmailLabel(inv: OrgInvitationListItem): string {
  return inv.email ?? OPEN_INVITATION_LABEL;
}

/** La columna «Acceso» de una invitación: la marca si la lleva, si no
 *  «Según su rol» (las tiendas estampadas no viajan en la lista). */
export function invitationAccessLabel(
  inv: OrgInvitationListItem,
  names: ReadonlyMap<string, string>,
  w: EquipoWords
): string {
  if (inv.scopeBrandId) return names.get(inv.scopeBrandId) ?? `Su ${w.brandLower}`;
  return "Según su rol";
}

// --- Tab Roles ----------------------------------------------------------

export type RoleRow = {
  key: string;
  label: string;
  /** «Qué ve». */
  sees: string;
  /** «Quién lo puede invitar»: etiquetas, o «Solo lo crea BitácoraPro». */
  inviters: string;
};

export const ONLY_BITACORAPRO = "Solo lo crea BitácoraPro";
/** La misma nota a mitad de frase («Gerente: solo lo crea BitácoraPro»):
 *  sólo baja la primera letra, el nombre propio se conserva. */
export const ONLY_BITACORAPRO_INLINE =
  ONLY_BITACORAPRO.charAt(0).toLocaleLowerCase("es-CL") + ONLY_BITACORAPRO.slice(1);

export function roleSees(entry: MemberRoleEntry, w: EquipoWords): string {
  switch (entry.scopeType) {
    case "organization":
      return ORG_WIDE_ACCESS;
    case "asset_group":
      return `Su ${w.groupOneLower} y ${w.assetManyLower} asignadas`;
    case "asset":
      return `Su ${w.assetOneLower}`;
    case "brand":
      return `Solo lectura de su ${w.brandLower}`;
    default:
      return ORG_WIDE_ACCESS;
  }
}

/** Los roles de la receta que pueden otorgar `target`, en el orden de la
 *  receta, según INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER. */
function recipeInvitersOf(recipe: EquipoRecipe, target: string): string[] {
  return recipe.memberRoles
    .filter((r) => {
      const grantable = (
        INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER as Record<string, readonly string[] | undefined>
      )[r.key];
      return grantable?.includes(target) ?? false;
    })
    .map((r) => roleLabelFor(r.key, recipe));
}

export function rolesTable(recipe: EquipoRecipe): RoleRow[] {
  const w = equipoWords(recipe);
  return recipe.memberRoles.map((r) => {
    const inviters = recipeInvitersOf(recipe, r.key);
    return {
      key: r.key,
      label: roleLabelFor(r.key, recipe),
      sees: roleSees(r, w),
      inviters: inviters.length > 0 ? inviters.join(", ") : ONLY_BITACORAPRO,
    };
  });
}

// --- Drawer «Invitar persona» -------------------------------------------

export type RoleOption = {
  key: Role;
  label: string;
  sees: string;
  /** false → deshabilitado con la nota «Solo lo crea BitácoraPro». */
  grantable: boolean;
};

/** Los roles de la receta en su orden. Los que el caller no puede
 *  otorgar por escalamiento van deshabilitados con la nota; los que la
 *  org no admite por tener una sola marca (SINGLE_BRAND_ORG) se ocultan,
 *  como hacía la pantalla vieja: no es una restricción de quien invita. */
export function roleOptions(
  recipe: EquipoRecipe,
  inviterRole: Role | null,
  opts: { platformAdmin: boolean; brandCount: number | null }
): RoleOption[] {
  const w = equipoWords(recipe);
  const out: RoleOption[] = [];
  for (const r of recipe.memberRoles) {
    if (!(ROLE_KEYS as readonly string[]).includes(r.key)) continue;
    const key = r.key as Role;
    const denial = invitationGrantDenial(inviterRole, key, recipe, opts);
    if (denial === "SINGLE_BRAND_ORG" || denial === "ROLE_NOT_IN_RECIPE") continue;
    out.push({
      key,
      label: roleLabelFor(key, recipe),
      sees: roleSees(r, w),
      grantable: denial === null,
    });
  }
  return out;
}

// Mismo piso que emailSchema del wire; la validación real es del servidor.
export function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export type ParsedEmails = {
  /** Únicos, en minúscula, en el orden escrito. */
  valid: string[];
  /** Lo que no parece un correo, tal como se escribió. */
  invalid: string[];
};

/** «Separa varios correos con coma»: también acepta punto y coma, espacio
 *  o salto de línea, porque es lo que llega al pegar desde una planilla. */
export function parseEmails(text: string): ParsedEmails {
  const valid: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/[,\s;]+/)) {
    const token = raw.trim();
    if (!token) continue;
    if (!isValidEmail(token)) {
      invalid.push(token);
      continue;
    }
    const email = token.toLowerCase();
    if (seen.has(email)) continue;
    seen.add(email);
    valid.push(email);
  }
  return { valid, invalid };
}

export type InviteOutcome = {
  sent: string[];
  failed: { email: string; message: string }[];
};

/** «Invitación enviada» / «3 invitaciones enviadas» / «2 enviadas · 1 con
 *  error». */
export function inviteToast(outcome: InviteOutcome): { message: string; tone: "ok" | "err" } {
  const sent = outcome.sent.length;
  const failed = outcome.failed.length;
  if (failed === 0) {
    return {
      message: sent === 1 ? "Invitación enviada" : `${sent} invitaciones enviadas`,
      tone: "ok",
    };
  }
  return {
    message: `${sent} ${sent === 1 ? "enviada" : "enviadas"} · ${failed} con error`,
    tone: "err",
  };
}

// Copiado de routes/Equipo.tsx (INVITE_ERROR_MESSAGES no está exportado y
// esa pantalla sigue sirviendo a postventa/arriendos sin cambios): los
// códigos de POST /organizations/me/invitations → es-CL. Si uno cambia
// allá tiene que cambiar acá.
export const INVITE_ERROR_MESSAGES: Record<string, string> = {
  ROLE_ESCALATION: "No tienes permiso para invitar con ese rol.",
  SINGLE_BRAND_ORG:
    "Esta organización no tiene más de una marca — no se puede invitar a un Gerente de marca.",
  ROLE_NOT_IN_RECIPE:
    "Ese rol ya no está disponible para invitar en esta organización. Actualiza la página e intenta de nuevo.",
  STORE_MANAGER_REQUIRES_EXACTLY_ONE_STORE:
    "Los jefes de tienda se asignan a exactamente una tienda. Elige una sola tienda e intenta de nuevo.",
  ASSET_OUT_OF_SCOPE:
    "No tienes acceso a una o más de las tiendas seleccionadas. Actualiza la página e intenta de nuevo.",
  NO_ASSIGNABLE_STORES:
    "No quedan tiendas sin asignar dentro de tu alcance. Contacta a un administrador.",
  BRAND_REQUIRED: "Elige la marca que va a gestionar esta persona.",
  BRAND_NOT_IN_ORG: "Esa marca no pertenece a tu organización.",
  BRAND_NOT_ALLOWED: "Este rol no lleva marca.",
  EMAIL_REQUIRED_FOR_ROLE: "Ingresa el correo de la persona.",
  INVITATION_SCOPE_DROPPED:
    "No pudimos guardar la marca de la invitación. Inténtalo de nuevo en unos minutos.",
  AFFILIATION_REQUIRED: "Elige la marca de este supervisor.",
  AFFILIATION_NOT_IN_ORG: "Esa marca no pertenece a tu organización.",
  AFFILIATION_MISMATCH: "Las tiendas elegidas son de otra marca.",
  AFFILIATION_NOT_ALLOWED: "Este rol no lleva una marca propia.",
  SUPERVISOR_SPANS_BRANDS:
    "Las tiendas elegidas son de más de una marca: un supervisor pertenece a una sola.",
  STORE_BRAND_UNRESOLVED:
    "Una de las tiendas elegidas no tiene marca. Asígnale una antes de invitar.",
};
export const BRAND_FIELD_ERROR_CODES: ReadonlySet<string> = new Set([
  "BRAND_REQUIRED",
  "BRAND_NOT_IN_ORG",
]);
export const AFFILIATION_FIELD_ERROR_CODES: ReadonlySet<string> = new Set([
  "AFFILIATION_REQUIRED",
  "AFFILIATION_NOT_IN_ORG",
  "AFFILIATION_MISMATCH",
]);
export const AFFILIATION_HINT =
  "El supervisor pertenece a una sola marca: es la organización que verá en el panel, y solo se le asignan tiendas de esa marca.";

export function inviteErrorMessage(err: unknown): string {
  const generic = "No pudimos enviar la invitación. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  return INVITE_ERROR_MESSAGES[err.code] ?? generic;
}

// --- Errores de las acciones de la tabla ---------------------------------

/** PATCH /auth/memberships/:id → frase. */
export function membershipErrorMessage(err: unknown): string {
  const generic = "No pudimos guardar el cambio. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "ADMIN_REQUIRED":
    case "ORG_POLICY_UNAVAILABLE":
      return "Tu rol no puede hacer este cambio.";
    case "SELF_DEACTIVATION_FORBIDDEN":
      return "No puedes desactivar tu propio acceso.";
    case "OWNER_DEACTIVATION_FORBIDDEN":
      return "El dueño de la organización no se puede desactivar.";
    case "MEMBERSHIP_NOT_FOUND":
      return "Esta persona ya no está en el equipo. Recarga la página.";
    default:
      return generic;
  }
}

/** POST /auth/invitations/:id/revoke|resend → frase. */
export function invitationActionErrorMessage(err: unknown): string {
  const generic = "No pudimos completar la acción. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "ADMIN_REQUIRED":
    case "ORG_POLICY_UNAVAILABLE":
      return "Tu rol no puede hacer este cambio.";
    case "INVITATION_NOT_FOUND":
      return "Esta invitación ya no está pendiente. Recarga la página.";
    case "INVITATION_HAS_NO_EMAIL":
      return "Esta invitación no tiene correo al que reenviar.";
    default:
      return generic;
  }
}

export const RESEND_EMAIL_FAILED = "No se pudo enviar el correo · vuelve a intentarlo";

// --- Drawer «Acceso de {nombre}» ----------------------------------------

export type ScopeDiff = { add: string[]; remove: string[] };

/** Lo que cambió en el selector: `add` lo marcado que no estaba, `remove`
 *  lo que estaba y se desmarcó. Vacío en ambos = nada que guardar. */
export function scopeDiff(
  originalIds: readonly string[],
  selectedIds: readonly string[]
): ScopeDiff {
  const original = new Set(originalIds);
  const selected = new Set(selectedIds);
  return {
    add: [...selected].filter((id) => !original.has(id)),
    remove: [...original].filter((id) => !selected.has(id)),
  };
}

export function hasScopeChanges(diff: ScopeDiff): boolean {
  return diff.add.length > 0 || diff.remove.length > 0;
}

export type OccupiedDetails = { assetId: string; unit: string | null };

/** El 409 ASSET_HOLDER_OCCUPIED trae la tienda ocupada en `details`. */
export function occupiedDetails(err: unknown): OccupiedDetails | null {
  if (!(err instanceof ApiError) || err.code !== "ASSET_HOLDER_OCCUPIED") return null;
  const d = err.details ?? {};
  const assetId = typeof d.assetId === "string" ? d.assetId : null;
  if (!assetId) return { assetId: "", unit: null };
  return { assetId, unit: typeof d.unit === "string" ? d.unit : null };
}

/** «AS-014 ya tiene jefe de tienda»: el rol del integrante en minúscula. */
export function occupiedMessage(
  details: OccupiedDetails,
  roleLabel: string,
  names: ReadonlyMap<string, string>,
  w: EquipoWords
): string {
  const unit = details.unit ?? names.get(details.assetId) ?? `Esa ${w.assetOneLower}`;
  return `${unit} ya tiene ${lower(roleLabel)}`;
}

/** PUT /organizations/me/members/:userId/scope → frase (el 409 ocupado
 *  tiene su propio camino con «Reemplazar»). */
export function scopeErrorMessage(err: unknown, w: EquipoWords): string {
  const generic = "No pudimos guardar el acceso. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "ADMIN_REQUIRED":
      return "Tu rol no puede hacer este cambio.";
    case "MEMBER_NOT_FOUND":
      return "Esta persona ya no está en el equipo. Recarga la página.";
    case "ASSET_NOT_FOUND":
      return `Una de las ${w.assetManyLower} elegidas ya no existe. Recarga la página.`;
    case "ROLE_NOT_ASSET_SCOPED":
      return `El acceso de este rol no se define por ${w.assetManyLower}.`;
    case "BRAND_MISMATCH":
      return `Las ${w.assetManyLower} elegidas son de otra ${w.brandLower}.`;
    case "STORE_BRAND_UNRESOLVED":
      return `Una de las ${w.assetManyLower} elegidas no tiene ${w.brandLower}. Asígnale una antes.`;
    case "ROSTER_UNAVAILABLE":
      return "No pudimos leer el equipo. Intenta de nuevo.";
    case "VALIDATION_ERROR":
      return `Revisa las ${w.assetManyLower} elegidas.`;
    default:
      return generic;
  }
}

export const SCOPE_SAVED_TOAST = "Acceso actualizado · aplica desde ahora";
export const SCOPE_HINT =
  "Quitar acceso no borra lo que esta persona ya registró; solo deja de ver esas";

// --- Drawer «Invitar en lote» --------------------------------------------
//
// Lo que el drawer pinta sale de aquí: la fila del preview más lo que el
// commit dijo de ella. Los mensajes por fila (`error.message` del preview,
// `message` del commit) son finales en el servidor y se muestran tal
// cual; aquí sólo se redactan los de nivel archivo y los conteos.

/** Estado de una fila en el drawer: los dos del preview y los dos que
 *  deja el commit. Una fila `failed` conserva su payload y se reintenta
 *  con el siguiente «Enviar»; una `sent` ya no viaja. */
export type BatchRowState = "ok" | "error" | "sent" | "failed";

export type BatchRowView = {
  row: number;
  email: string;
  roleLabel: string;
  /** Lo que se imprime en «Acceso»: la etiqueta que armó el servidor (el
   *  nombre de la tienda o zona, «3 tiendas sin asignar», o la celda cruda
   *  cuando no resolvió); «—» sólo cuando viene vacía. */
  accessLabel: string;
  state: BatchRowState;
  /** El motivo cuando `state` es error o failed; null en ok y sent. */
  message: string | null;
  payload: InvitationBatchPreviewRow["payload"];
};

/** Clave con la que se recuerda un correo ya enviado: el preview trae la
 *  celda tal cual se escribió; el commit, el del payload (minúsculas). */
export function batchEmailKey(email: string): string {
  return email.trim().toLowerCase();
}

const NO_EMAILS: ReadonlySet<string> = new Set();

/** Las filas del preview tal como las pinta el drawer. `alreadySent` son
 *  los correos que salieron en un commit anterior de este mismo drawer:
 *  una fila lista con uno de ellos nace `sent` (ni el preview ni auth-svc
 *  comprueban invitaciones pendientes al mismo correo, así que «Volver» →
 *  «Revisar» → «Enviar» sobre la misma planilla la duplicaría). */
export function batchRowsOf(
  preview: InvitationBatchPreviewResponse,
  alreadySent: ReadonlySet<string> = NO_EMAILS
): BatchRowView[] {
  return preview.rows.map((r) => ({
    row: r.row,
    email: r.email,
    roleLabel: r.roleLabel,
    accessLabel: r.scope.label === "" ? "—" : r.scope.label,
    state: r.status === "ok" && alreadySent.has(batchEmailKey(r.email)) ? "sent" : r.status,
    message: r.error?.message ?? null,
    payload: r.payload,
  }));
}

/** Los correos enviados hasta ahora más los de este commit. */
export function batchSentEmails(
  previous: ReadonlySet<string>,
  result: InvitationBatchCommitResponse
): ReadonlySet<string> {
  const next = new Set(previous);
  for (const s of result.sent) next.add(batchEmailKey(s.email));
  return next;
}

/** Las filas que viajan en el próximo commit: las `ok` del preview y las
 *  `failed` de un commit anterior (reintentar no duplica: las `sent` ya
 *  no están). */
export function pendingBatchRows(rows: readonly BatchRowView[]): BatchRowView[] {
  return rows.filter((r) => (r.state === "ok" || r.state === "failed") && r.payload !== undefined);
}

export function batchCommitBody(rows: readonly BatchRowView[]): InvitationBatchCommitRequest {
  return {
    rows: pendingBatchRows(rows).map((r) => ({
      row: r.row,
      payload: r.payload as OrgSelfServiceInvitationCreateRequest,
    })),
  };
}

/** Aplica el resultado del commit sobre las filas: `sent` y `failed` por
 *  número de fila; lo que el servidor no nombró no cambia. */
export function applyBatchCommit(
  rows: readonly BatchRowView[],
  result: InvitationBatchCommitResponse
): BatchRowView[] {
  const sent = new Set(result.sent.map((s) => s.row));
  const failed = new Map(result.failed.map((f) => [f.row, f.message] as const));
  return rows.map((r) => {
    if (sent.has(r.row)) return { ...r, state: "sent", message: null };
    const message = failed.get(r.row);
    if (message !== undefined) return { ...r, state: "failed", message };
    return r;
  });
}

export type BatchCounts = Record<BatchRowState, number>;

export function batchCounts(rows: readonly BatchRowView[]): BatchCounts {
  const counts: BatchCounts = { ok: 0, error: 0, sent: 0, failed: 0 };
  for (const r of rows) counts[r.state] += 1;
  return counts;
}

export type BatchPill = { tone: "ok" | "danger"; text: string };

/** Los pills de `ag-summary`: «7 listas para enviar» / «1 con error», y
 *  tras un commit «7 enviadas» / «2 no enviadas». Antes del commit el de
 *  listas se muestra siempre («0 listas para enviar» junto al botón
 *  deshabilitado, como pide el handoff); los demás, y ése después del
 *  commit, se omiten cuando cuentan cero. */
export function batchSummaryPills(counts: BatchCounts): BatchPill[] {
  const pills: BatchPill[] = [];
  const beforeCommit = counts.sent === 0 && counts.failed === 0;
  if (counts.ok > 0 || beforeCommit) {
    pills.push({
      tone: "ok",
      text: `${counts.ok} ${counts.ok === 1 ? "lista" : "listas"} para enviar`,
    });
  }
  if (counts.sent > 0) {
    pills.push({
      tone: "ok",
      text: `${counts.sent} ${counts.sent === 1 ? "enviada" : "enviadas"}`,
    });
  }
  if (counts.error > 0) pills.push({ tone: "danger", text: `${counts.error} con error` });
  if (counts.failed > 0) {
    pills.push({
      tone: "danger",
      text: `${counts.failed} no ${counts.failed === 1 ? "enviada" : "enviadas"}`,
    });
  }
  return pills;
}

/** «{fileName} · 8 filas» del `ag-file`. */
export function batchFileLabel(fileName: string, rowCount: number): string {
  return `${fileName} · ${rowCount} ${rowCount === 1 ? "fila" : "filas"}`;
}

/** El eyebrow de la tarjeta de una fila que no viaja: «Fila 5 no se
 *  enviará» antes del commit, «Fila 5 no se envió» después. */
export function batchCardEyebrow(r: BatchRowView): string {
  return r.state === "failed" ? `Fila ${r.row} no se envió` : `Fila ${r.row} no se enviará`;
}

export const BATCH_MAX_ERROR_CARDS = 3;

/** Las tarjetas de error que se muestran (las primeras 3) y el resto
 *  resumido: «y 2 filas más con error». */
export function batchErrorCards(rows: readonly BatchRowView[]): {
  shown: BatchRowView[];
  moreLabel: string | null;
} {
  const errored = rows.filter((r) => r.state === "error" || r.state === "failed");
  const shown = errored.slice(0, BATCH_MAX_ERROR_CARDS);
  const more = errored.length - shown.length;
  return {
    shown,
    moreLabel: more > 0 ? `y ${more} ${more === 1 ? "fila más" : "filas más"} con error` : null,
  };
}

/** Texto del pill «Estado» de la tabla, además del color. */
export function batchRowStateLabel(state: BatchRowState): string {
  switch (state) {
    case "ok":
      return "Lista";
    case "error":
      return "Error";
    case "sent":
      // Estado de la fila de la planilla tras el commit (la invitación
      // salió), no el estado postventa:
      // fitness-allow: postventa-labels-single-source
      return "Enviada";
    case "failed":
      return "No enviada";
  }
}

export function batchRowStateTone(state: BatchRowState): "ok" | "danger" {
  return state === "ok" || state === "sent" ? "ok" : "danger";
}

/** «Enviar 7 invitaciones» / «Enviar 1 invitación»; con cero queda el
 *  plural y el botón deshabilitado. */
export function batchSendLabel(pending: number): string {
  return `Enviar ${pending} ${pending === 1 ? "invitación" : "invitaciones"}`;
}

export type BatchOutcome = {
  /** Invitaciones que salieron en este commit. */
  sent: number;
  /** Filas que no salieron: con error en el preview + fallidas en el commit. */
  omitted: number;
  /** Las fallidas del commit (parte de `omitted`): si hay, el drawer sigue abierto. */
  failed: number;
};

/** «7 invitaciones enviadas · 1 fila omitida». Tono de error si alguna
 *  fila del commit falló o no salió ninguna. */
export function batchToast(outcome: BatchOutcome): { message: string; tone: "ok" | "err" } {
  const sent = `${outcome.sent} ${outcome.sent === 1 ? "invitación enviada" : "invitaciones enviadas"}`;
  const omitted =
    outcome.omitted > 0
      ? ` · ${outcome.omitted} ${outcome.omitted === 1 ? "fila omitida" : "filas omitidas"}`
      : "";
  return {
    message: `${sent}${omitted}`,
    tone: outcome.failed > 0 || outcome.sent === 0 ? "err" : "ok",
  };
}

export const BATCH_EMPTY_FILE = "La planilla no tiene filas";

/** Errores de nivel archivo de POST …/batch/preview (y de la descarga de
 *  la plantilla) → frase. Los 422 traen un mensaje final del servidor;
 *  el resto se redacta aquí. */
export function batchFileErrorMessage(err: unknown): string {
  const generic = "No pudimos leer la planilla. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "FILE_TOO_LARGE":
      return "La planilla pesa más de 5 MB. Divídela en dos.";
    case "BATCH_FILE_INVALID":
    case "BATCH_TOO_LARGE":
      return err.message || generic;
    case "FILE_REQUIRED":
      return "Elige una planilla antes de revisar.";
    case "RATE_LIMITED": {
      const secs = err.details?.retryAfterSeconds;
      return typeof secs === "number" && secs > 0
        ? `Demasiados intentos seguidos. Espera ${secs} ${secs === 1 ? "segundo" : "segundos"} y vuelve a intentarlo.`
        : "Demasiados intentos seguidos. Espera un momento y vuelve a intentarlo.";
    }
    case "ROLE_ESCALATION":
      return "Tu rol no puede invitar personas.";
    case "NETWORK":
      return "Sin conexión. Revisa tu red e intenta de nuevo.";
    default:
      return generic;
  }
}

/** POST …/batch/commit falló entero (nada se envió) → frase. */
export function batchCommitErrorMessage(err: unknown): string {
  const generic = "No pudimos enviar las invitaciones. Intenta de nuevo.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "RATE_LIMITED":
      return "Ya enviaste varios lotes seguidos. Espera unos minutos y vuelve a intentarlo.";
    case "ROLE_ESCALATION":
      return "Tu rol no puede invitar personas.";
    case "VALIDATION_ERROR":
      return "Las filas ya no tienen la forma esperada. Vuelve a revisar la planilla.";
    case "NETWORK":
      return "Sin conexión. Revisa tu red e intenta de nuevo.";
    default:
      return generic;
  }
}

/** GET …/batch/template falló → frase. */
export function batchTemplateErrorMessage(err: unknown): string {
  if (err instanceof ApiError && (err.code === "RATE_LIMITED" || err.code === "NETWORK")) {
    return batchFileErrorMessage(err);
  }
  return "No pudimos descargar la plantilla. Intenta de nuevo.";
}
